import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  TRANSFER_TYPES,
  createTransfer,
  employeeLabelOf,
  listDepts,
  listEmployees,
  listTerritories,
  type DeptRow,
  type EmployeeRow,
  type TerritoryRow,
  type TransferType,
} from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso, toOptions } from './orgKit.js';

export function TransferFormPage(): ReactNode {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const presetEmployee = params.get('employeeId') ?? '';

  const [employeeId, setEmployeeId] = useState(presetEmployee);
  const [transferType, setTransferType] = useState<TransferType>('TERRITORY');
  const [fromTerritoryId, setFromTerritoryId] = useState('');
  const [toTerritoryId, setToTerritoryId] = useState('');
  const [fromManagerId, setFromManagerId] = useState('');
  const [toManagerId, setToManagerId] = useState('');
  const [fromDeptId, setFromDeptId] = useState('');
  const [toDeptId, setToDeptId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(todayIso());

  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [territories, setTerritories] = useState<TerritoryRow[]>([]);
  const [depts, setDepts] = useState<DeptRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listEmployees(token).then((r) => setEmployees(r.data)).catch((e) => setError((e as Error).message));
    listTerritories(token).then((r) => setTerritories(r.data)).catch((e) => setError((e as Error).message));
    listDepts(token).then((r) => setDepts(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  // Prefill the "from" side from the chosen employee's current placement.
  useEffect(() => {
    if (!employeeId) return;
    const emp = employees.find((e) => e.empExtId === employeeId);
    if (!emp) return;
    if (emp.defaultTerritoryId) setFromTerritoryId((v) => v || emp.defaultTerritoryId!);
    if (emp.deptId) setFromDeptId((v) => v || emp.deptId!);
  }, [employeeId, employees]);

  const showTerritory = transferType === 'TERRITORY' || transferType === 'FULL';
  const showManager = transferType === 'MANAGER' || transferType === 'FULL';
  const showDept = transferType === 'DEPARTMENT' || transferType === 'FULL';

  async function submit(): Promise<void> {
    if (!employeeId) { setError(t('org.transfer.form.employeeReq')); return; }
    if (showTerritory && !toTerritoryId) { setError(t('org.transfer.form.toTerritoryReq')); return; }
    if (showManager && !toManagerId) { setError(t('org.transfer.form.toManagerReq')); return; }
    if (showDept && !toDeptId) { setError(t('org.transfer.form.toDeptReq')); return; }
    setError(null);
    setBusy(true);
    try {
      await createTransfer(token, {
        employeeId,
        transferType,
        fromTerritoryId: showTerritory ? (fromTerritoryId || undefined) : undefined,
        toTerritoryId: showTerritory ? (toTerritoryId || undefined) : undefined,
        fromManagerId: showManager ? (fromManagerId || undefined) : undefined,
        toManagerId: showManager ? (toManagerId || undefined) : undefined,
        fromDeptId: showDept ? (fromDeptId || undefined) : undefined,
        toDeptId: showDept ? (toDeptId || undefined) : undefined,
        transferReason: transferReason.trim() || undefined,
        effectiveDate,
      });
      navigate('/org/transfers');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const employeeOptions = employees.map((e) => ({ value: e.empExtId, label: employeeLabelOf(e) }));
  const territoryOptions = useMemo(
    () => territories.map((t) => ({ value: t.territoryId, label: `${t.territoryCode} — ${t.territoryName}` })),
    [territories],
  );
  const deptOptions = depts.map((d) => ({ value: d.deptId, label: `${d.deptCode} — ${d.deptName}` }));

  return (
    <FormShell label="transfer-form" title={t('org.transfer.form.title')} error={error} onSubmit={submit}>
      <SelectField label={t('org.field.employee')} name="employeeId" value={employeeId} onChange={setEmployeeId} options={employeeOptions} required />
      <SelectField label={t('org.field.transferType')} name="transferType" value={transferType} onChange={(v) => setTransferType(v as TransferType)} options={toOptions(TRANSFER_TYPES)} required />
      {showTerritory && (
        <>
          <SelectField label={t('org.transfer.form.fromTerritory')} name="fromTerritoryId" value={fromTerritoryId} onChange={setFromTerritoryId} options={territoryOptions} placeholder={t('org.transfer.form.currentNone')} />
          <SelectField label={t('org.transfer.form.toTerritory')} name="toTerritoryId" value={toTerritoryId} onChange={setToTerritoryId} options={territoryOptions} required />
        </>
      )}
      {showManager && (
        <>
          <SelectField label={t('org.transfer.form.fromManager')} name="fromManagerId" value={fromManagerId} onChange={setFromManagerId} options={employeeOptions} placeholder={t('org.transfer.form.currentNone')} />
          <SelectField label={t('org.transfer.form.toManager')} name="toManagerId" value={toManagerId} onChange={setToManagerId} options={employeeOptions} required />
        </>
      )}
      {showDept && (
        <>
          <SelectField label={t('org.transfer.form.fromDept')} name="fromDeptId" value={fromDeptId} onChange={setFromDeptId} options={deptOptions} placeholder={t('org.transfer.form.currentNone')} />
          <SelectField label={t('org.transfer.form.toDept')} name="toDeptId" value={toDeptId} onChange={setToDeptId} options={deptOptions} required />
        </>
      )}
      <Field label={t('org.field.reason')} name="transferReason" value={transferReason} onChange={setTransferReason} placeholder={t('org.common.optional')} />
      <Field label={t('org.field.effectiveDate')} name="effectiveDate" value={effectiveDate} onChange={setEffectiveDate} type="date" required />
      <SaveBar busy={busy} saveLabel={t('org.transfer.form.create')} cancelTo="/org/transfers" />
    </FormShell>
  );
}
